/**
 * Zod schemas for runtime validation of external API responses.
 * Only the most security-critical boundaries are validated here.
 * Other boundaries will be added incrementally in future waves.
 */

import { z } from 'zod';

const UserPermissionsSchema = z.object({
  can_access_square: z.boolean().optional(),
  can_access_ai: z.boolean().optional(),
  can_manage_users: z.boolean().optional(),
  can_manage_tokens: z.boolean().optional(),
  role: z.string().optional(),
});

const AuthOrganizationSchema = z.object({
  id: z.string(),
  name: z.string(),
  display_name: z.string().nullish(),
  slug: z.string().nullish(),
  square_environment: z.string(),
  min_required_images: z.number().nullish(),
  image_ordering_patterns: z
    .array(z.object({ pattern: z.string(), order: z.number() }))
    .nullish(),
  // Sensitive tokens intentionally excluded — they must never enter frontend
  // auth state. Admin pages get tokens via the admin API instead.
});

const UserDataSchema = z.object({
  id: z.union([z.string(), z.number()]).transform(String),
  email: z.string(),
  full_name: z.string().nullish(),
  picture: z.string().nullish(),
  is_admin: z.boolean(),
  can_access_square: z.boolean(),
  can_access_ai: z.boolean(),
});

/**
 * Schema for the /api/auth/verify response.
 * Parsed in useAuth before trusting any field.
 */
export const AuthVerifyResponseSchema = z.object({
  user: UserDataSchema,
  organization: AuthOrganizationSchema.nullish(),
  permissions: UserPermissionsSchema.optional(),
});

export type AuthVerifyResponse = z.infer<typeof AuthVerifyResponseSchema>;
